import { useEffect } from 'react'
import { match } from 'ts-pattern'
import { CartContents } from '@/components/domains/cart/CartContents'
import { LayoutHeader } from '@/components/domains/layouts'
import { LayoutSelectTab } from '@/components/domains/layouts/LayoutSelectTab'
import { LikeProductList } from '@/components/domains/mypage/favorites/like-product-list'
import ROUTES from '@/constants/legacy/constRoutes'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import { useCartContainer } from '@/containers/cart/cart.context'
import { ShippingForCart } from '@/containers/cart/useCartContents'
import useCartEventTracker from '@/containers/event-tracker/cart.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import PageFrequentlyList from '@/pages/mypage/frequently-list.page'

const CartTabValue = {
  cart: 'cart',
  like: 'like',
  frequently: 'frequently',
} as const

type CartTabValue = typeof CartTabValue[keyof typeof CartTabValue]

const PageCart = () => {
  const {
    query: { tab },
    replace,
  } = useCustomRouter()
  const { trackViewCartLikeTabEvent, trackViewCartFrequentTabEvent, trackViewCartEvent2 } = useCartEventTracker()

  const { cartCount, likeCount, frequentlyCount } = useCartContainer()

  const tabValue = tab && CartTabValue[tab as CartTabValue] ? (tab as CartTabValue) : CartTabValue.cart

  const tabItems = [
    { id: CartTabValue.cart, title: '장바구니', count: cartCount },
    { id: CartTabValue.like, title: '찜한 상품', count: likeCount },
    { id: CartTabValue.frequently, title: '자주 산 상품', count: frequentlyCount },
  ]

  const handleTabChange = (id: string) => {
    match(id as CartTabValue)
      .with(CartTabValue.cart, () => {}) // CartContentsLegacy 컴포넌트에서 trackViewCartTabEvent 호출
      .with(CartTabValue.like, () => {
        trackViewCartLikeTabEvent({
          countProductCount: cartCount,
          frequentProductCount: frequentlyCount,
          likeProductCount: likeCount,
        })
      })
      .with(CartTabValue.frequently, () => {
        trackViewCartFrequentTabEvent({
          countProductCount: cartCount,
          frequentProductCount: frequentlyCount,
          likeProductCount: likeCount,
        })
      })
    replace({
      query: { tab: id, [QueryStringKeyEnum.PrevPath]: ROUTES.CART, [QueryStringKeyEnum.TabClick]: id },
    })
  }

  const handleViewCartTracker = ({
    productAmount,
    shippings,
  }: {
    productAmount: number
    shippings: ShippingForCart[]
  }) => {
    trackViewCartEvent2({
      productAmount: productAmount,
      cartCount: cartCount,
      likeCount: likeCount,
      frequentlyCount: frequentlyCount,
      shippings: shippings,
    })
  }

  useEffect(() => {
    if (tabValue) {
      return
    }
    replace({
      query: { tab: CartTabValue.cart },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!tabValue) {
    return null
  }

  return (
    <>
      <LayoutHeader />
      <LayoutSelectTab onChange={handleTabChange} items={tabItems} value={tabValue} />
      {match(tabValue)
        .with(CartTabValue.cart, () => <CartContents onViewCartTracker={handleViewCartTracker} />)
        .with(CartTabValue.like, () => <LikeProductList title="찜한 상품" />)
        .with(CartTabValue.frequently, () => <PageFrequentlyList isPage={false} />)
        .exhaustive()}
    </>
  )
}

export default PageCart
